import React, { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { DataOverview, Notification } from '@vwfs-bronson/bronson-react';
import { NotificationStatus } from '@cp-shared-19/frontend-ui';
import { ContactDetails } from 'cpfr-common';
import { NoConnectionNotification } from '../../../notifications/no-connection/NoConnectionNotification';
import { NotFoundError } from '../../not-found-error/NotFoundError';
import { EditStatus } from '../edit-view/EditView';
import { ContactList } from './ContactList';
import { NotificationForEditStatus } from './NotificationForEditStatus';
import { ModificationStatus } from '../../modification-status';

export type ConsultViewProps = {
    contactDetails?: ContactDetails;
    startEditing?: () => void;
    editStatus: EditStatus;
    changePending: boolean;
};

export const isEmptyContact = (contactDetails?: ContactDetails): boolean => {
    return !contactDetails || isEmpty(contactDetails);
};

export const ConsultView: React.FC<ConsultViewProps> = ({
    contactDetails,
    startEditing,
    editStatus,
    changePending,
}) => {
    const { t } = useTranslation('my-profile');
    const translationPrefix = 'contact-details.consult-view';

    const triggerEdit = useCallback(
        (event?: { preventDefault?: () => void }) => {
            event?.preventDefault?.();
            startEditing?.();
        },
        [startEditing],
    );

    const handleEditKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                triggerEdit(event);
            }
        },
        [triggerEdit],
    );

    return (
        <DataOverview
            titleLevel={2}
            title={t(`${translationPrefix}.title`)}
            buttonLabel={t('contact-details.consult-view.edit-button')}
            buttonProps={{
                tabIndex: 0,
                onClick: triggerEdit,
                onKeyDown: handleEditKeyDown,
                testId: 'editButton',
                disabled: changePending,
            }}
        >
            {changePending && <ModificationStatus />}

            {!contactDetails ? (
                <NotFoundError />
            ) : isEmptyContact(contactDetails) ? (
                <NoConnectionNotification />
            ) : (
                <>
                    {!changePending && (
                        <>
                            <Notification
                                status={NotificationStatus.info}
                                testId="contact-datails-info-alert"
                                className="u-mb"
                                title={''}
                                hideTitle={true}
                                visible
                                showCloseButton={false}
                            >
                                {t(`${translationPrefix}.info-alert`)}
                            </Notification>
                            <ContactList contactDetails={contactDetails} />
                        </>
                    )}

                    <NotificationForEditStatus editStatus={editStatus} />
                </>
            )}
        </DataOverview>
    );
};
